import { Button } from "@/components/ui/button";
import { secondaryButtonClasses } from "@/constants/ui";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";
import { DURATION_STEP_MINUTES, MAX_DURATION_MINUTES, MIN_DURATION_MINUTES } from "../types";
import { formatDuration } from "../utils";

interface DurationRockerProps {
  value: number; // Duration in minutes
  onChange: (minutes: number) => void;
  min?: number; // Default: 30
  max?: number; // Default: 1440 (24 hours)
  step?: number; // Default: 30
  disabled?: boolean;
}

export const DurationRocker = ({
  value,
  onChange,
  min = MIN_DURATION_MINUTES,
  max = MAX_DURATION_MINUTES,
  step = DURATION_STEP_MINUTES,
  disabled = false,
}: DurationRockerProps) => {
  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const canDecrement = value > min && !disabled;
  const canIncrement = value < max && !disabled;

  return (
    <div className="flex items-center gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDecrement}
        disabled={!canDecrement}
        aria-label="Decrease duration by 30 minutes"
        className={cn(secondaryButtonClasses, "h-9 w-9 p-0")}
      >
        <Minus className="h-4 w-4" />
      </Button>

      <div className="min-w-[80px] text-center font-medium text-lg tabular-nums bg-white border border-gray-200 rounded min-h-[34px] pt-0.5">
        {formatDuration(value)}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleIncrement}
        disabled={!canIncrement}
        aria-label="Increase duration by 30 minutes"
        className={cn(secondaryButtonClasses, "h-9 w-9 p-0")}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default DurationRocker;
